import { useMutation, useQuery, useQueryClient, type UseQueryOptions } from '@tanstack/react-query';

import { api, withOptionalCompanyId } from '@/shared/api/base';
import { routes, useAuthBootstrap } from '@/shared/lib';

import type {
  CreateTariffRequestMutationParams,
  CreateTariffRequestResult,
  SupportMessageCreateRequest,
  SupportSummaryResponse,
  SupportTicket,
  SupportTicketCreateRequest,
  SupportTicketListItem,
  SupportTicketListResponse,
  SupportTicketMessageMutationResponse,
} from './types';

export const supportQueryKeys = {
  root: () => ['support'] as const,
  summary: () => ['support', 'summary'] as const,
  tickets: () => ['support', 'tickets'] as const,
  detail: (ticketId: string) => ['support', 'tickets', ticketId] as const,
};

export async function getSupportSummary(): Promise<SupportSummaryResponse> {
  const { data } = await api.instance.get<SupportSummaryResponse>(
    '/api/v1/support/summary',
    withOptionalCompanyId(undefined),
  );

  return data ?? { unread_count: 0 };
}

export async function listSupportTickets(): Promise<SupportTicketListResponse> {
  const { data } = await api.instance.get<SupportTicketListResponse>(
    '/api/v1/support/tickets',
    withOptionalCompanyId(undefined),
  );

  return data ?? { items: [] };
}

export async function getSupportTicket(ticketId: string): Promise<SupportTicket> {
  const { data } = await api.instance.get<SupportTicket>(
    `/api/v1/support/tickets/${ticketId}`,
    withOptionalCompanyId(undefined),
  );

  if (!data) {
    throw new Error('Support ticket fetch failed');
  }

  return data;
}

export async function createSupportTicket(
  payload: SupportTicketCreateRequest,
): Promise<SupportTicket> {
  const { data } = await api.instance.post<SupportTicket>(
    '/api/v1/support/tickets',
    payload,
    withOptionalCompanyId(undefined),
  );

  if (!data) {
    throw new Error('Support ticket creation failed');
  }

  return data;
}

export async function createTariffRequestTicket(
  payload: SupportTicketCreateRequest,
): Promise<CreateTariffRequestResult> {
  const response = await api.instance.post<SupportTicket>(
    '/api/v1/support/tickets',
    payload,
    withOptionalCompanyId(undefined),
  );
  const ticket = response.data;

  if (!ticket) {
    throw new Error('Tariff request creation failed');
  }

  return {
    ticket,
    reusedExisting: response.status === 200,
  };
}

export async function replyToSupportTicket(
  ticketId: string,
  payload: SupportMessageCreateRequest,
): Promise<SupportTicketMessageMutationResponse> {
  const { data } = await api.instance.post<SupportTicketMessageMutationResponse>(
    `/api/v1/support/tickets/${ticketId}/messages`,
    payload,
    withOptionalCompanyId(undefined),
  );

  if (!data) {
    throw new Error('Support ticket reply failed');
  }

  return data;
}

export async function markSupportTicketRead(ticketId: string): Promise<SupportTicket> {
  const { data } = await api.instance.post<SupportTicket>(
    `/api/v1/support/tickets/${ticketId}/read`,
    undefined,
    withOptionalCompanyId(undefined),
  );

  if (!data) {
    throw new Error('Support ticket read acknowledgement failed');
  }

  return data;
}

export const sortSupportTicketsByActivity = (tickets: SupportTicketListItem[]) =>
  [...tickets].sort((left, right) => {
    const leftTimestamp = Date.parse(left.last_message_at ?? left.updated_at ?? left.created_at);
    const rightTimestamp = Date.parse(
      right.last_message_at ?? right.updated_at ?? right.created_at,
    );

    if (leftTimestamp === rightTimestamp) {
      return Date.parse(right.updated_at) - Date.parse(left.updated_at);
    }

    return rightTimestamp - leftTimestamp;
  });

export const upsertSupportTicketList = (
  current: SupportTicketListResponse | undefined,
  ticket: SupportTicket,
): SupportTicketListResponse => {
  const filtered = (current?.items ?? []).filter((existing) => existing.id !== ticket.id);
  const nextListItem: SupportTicketListItem = ticket;

  return {
    items: sortSupportTicketsByActivity([...filtered, nextListItem]),
  };
};

export const patchSupportTicketList = (
  current: SupportTicketListResponse | undefined,
  ticketId: string,
  update: (ticket: SupportTicketListItem) => SupportTicketListItem,
): SupportTicketListResponse | undefined => {
  if (!current) {
    return current;
  }

  return {
    items: sortSupportTicketsByActivity(
      current.items.map((ticket) => (ticket.id === ticketId ? update(ticket) : ticket)),
    ),
  };
};

export const useSupportTicketsQuery = () => {
  const { hasToken } = useAuthBootstrap();

  return useQuery({
    queryKey: supportQueryKeys.tickets(),
    queryFn: listSupportTickets,
    enabled: hasToken,
  });
};

export const useSupportSummaryQuery = (
  options?: Omit<UseQueryOptions<SupportSummaryResponse>, 'queryKey' | 'queryFn'>,
) => {
  const { hasToken } = useAuthBootstrap();
  const { enabled: enabledOption, ...rest } = options ?? {};

  return useQuery({
    ...rest,
    queryKey: supportQueryKeys.summary(),
    queryFn: getSupportSummary,
    enabled: Boolean(hasToken && (enabledOption ?? true)),
  });
};

export const useSupportTicketQuery = (ticketId: string | null) => {
  const { hasToken } = useAuthBootstrap();

  return useQuery({
    queryKey: supportQueryKeys.detail(ticketId ?? 'missing'),
    queryFn: () => getSupportTicket(ticketId ?? ''),
    enabled: hasToken && Boolean(ticketId),
  });
};

export const useCreateSupportTicketMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createSupportTicket,
    onSuccess: (ticket) => {
      queryClient.setQueryData(supportQueryKeys.detail(ticket.id), ticket);
      queryClient.setQueryData<SupportTicketListResponse | undefined>(
        supportQueryKeys.tickets(),
        (current) => upsertSupportTicketList(current, ticket),
      );
      queryClient.invalidateQueries({ queryKey: supportQueryKeys.summary() });
    },
  });
};

export const useCreateTariffRequestMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ planCode, companyId, comment, source }: CreateTariffRequestMutationParams) =>
      createTariffRequestTicket({
        ticket_type: 'tariff_request',
        message: comment?.trim() || `Хочу оформить тариф ${planCode}`,
        company_id: companyId ?? undefined,
        requested_plan_code: planCode,
        source: {
          page: routes.TARIFFS,
          entrypoint: source,
          metadata: {},
        },
      }),
    onSuccess: ({ ticket }) => {
      queryClient.setQueryData(supportQueryKeys.detail(ticket.id), ticket);
      queryClient.setQueryData<SupportTicketListResponse | undefined>(
        supportQueryKeys.tickets(),
        (current) => upsertSupportTicketList(current, ticket),
      );
      queryClient.invalidateQueries({ queryKey: supportQueryKeys.summary() });
    },
  });
};

export const useReplyToSupportTicketMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ ticketId, body }: { ticketId: string; body: string }) =>
      replyToSupportTicket(ticketId, { body }),
    onSuccess: ({ ticket }) => {
      queryClient.setQueryData(supportQueryKeys.detail(ticket.id), ticket);
      queryClient.setQueryData<SupportTicketListResponse | undefined>(
        supportQueryKeys.tickets(),
        (current) => upsertSupportTicketList(current, ticket),
      );
      queryClient.invalidateQueries({ queryKey: supportQueryKeys.summary() });
    },
  });
};

export const useMarkSupportTicketReadMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (ticketId: string) => markSupportTicketRead(ticketId),
    onSuccess: (ticket) => {
      queryClient.setQueryData(supportQueryKeys.detail(ticket.id), ticket);
      queryClient.setQueryData<SupportTicketListResponse | undefined>(
        supportQueryKeys.tickets(),
        (current) => upsertSupportTicketList(current, ticket),
      );
      queryClient.invalidateQueries({ queryKey: supportQueryKeys.summary() });
    },
  });
};
